import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, Download, ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { DocumentRenderer } from "../components/DocumentRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slug -> document id mapping. Slugs are stable, friendly URLs we share publicly.
const PUBLIC_DOC_SLUGS = {
  "partner-referral-guide": "partner-referral-guide",
  "police-referral-guide": "police-referral-one-pager",
  "police-referral-one-pager": "police-referral-one-pager",
  "law-enforcement-training": "lea-training-human-trafficking",
  "lea-training-human-trafficking": "lea-training-human-trafficking",
};

const TITLES = {
  "partner-referral-guide": "Partner Referral Guide",
  "police-referral-one-pager": "Police Officer Referral One-Pager",
  "lea-training-human-trafficking": "Law Enforcement Training — Human Trafficking Brief",
};

export default function PublicDocumentPage() {
  const { slug } = useParams();
  const docId = PUBLIC_DOC_SLUGS[slug];
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDoc = useCallback(async () => {
    if (!docId) {
      setError("Document not found.");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/public/documents/${docId}`);
      setDoc(res.data);
      document.title = `${TITLES[docId] || res.data.title} — The Pillar Project`;
    } catch {
      setError("This document is not available.");
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/public/documents/${docId}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (doc?.title || docId).toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-|-$/g, "");
      a.download = `${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Silent fail — user can still use Print / Save as PDF fallback.
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-warm-600" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <AlertCircle className="w-10 h-10 text-warm-700 mb-3" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
          {error || "Document not found"}
        </h1>
        <Link to="/" className="text-warm-700 hover:underline text-sm mt-4">← Back to The Pillar Project</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      {/* Toolbar — hidden on print */}
      <div className="print-hide max-w-[900px] mx-auto px-4 mb-5 flex items-center justify-between flex-wrap gap-3">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-warm-700" data-testid="public-doc-home-link">
          <ArrowLeft className="w-4 h-4" /> The Pillar Project
        </Link>
        <div className="flex items-center gap-3">
          <p className="text-xs text-gray-500 hidden md:block">
            One-click PDF download, or use Print for a browser-native copy.
          </p>
          <Button
            onClick={handleDownloadPdf}
            data-testid="public-doc-download-btn"
            variant="outline"
            className="border-warm-300 text-warm-700 hover:bg-warm-50 gap-2"
          >
            <Download className="w-4 h-4" /> Download PDF
          </Button>
          <Button
            onClick={handlePrint}
            data-testid="public-doc-print-btn"
            className="bg-warm-700 hover:bg-warm-800 text-white gap-2"
          >
            <Printer className="w-4 h-4" /> Print
          </Button>
        </div>
      </div>

      {/* Document — wrapped in .doc-print-area so the existing print CSS applies */}
      <div className="doc-print-area overflow-x-auto shadow-lg max-w-[900px] mx-auto bg-white">
        <DocumentRenderer doc={doc} />
      </div>

      <div className="print-hide max-w-[900px] mx-auto px-4 mt-6 text-center text-xs text-gray-500">
        The Pillar Project Corporation — Registered 501(c)(3) Tax-Exempt Nonprofit
        <br />
        <a href="/" className="text-warm-700 hover:underline">thepillarprojectbaltimore.org</a>
      </div>
    </div>
  );
}
